import { useMemo } from 'react';
import type { ActorModel } from './ActorGenerationWizard';
import { usePortalDropdown } from '../../generation/scenario/hooks/usePortalDropdown';
import { useGenerationModels } from '../../../features/generation-models/hooks';
import {
  ModelDropdownItem,
  ModelDropdownMenuShell,
  ModelDropdownSectionHeader,
  ModelDropdownSeparator,
} from '../../generation/common/ModelDropdownMenu';
import styles from './steps/Steps.module.css';

type Props = {
  /** メインボタンのラベル（例: 生成 / リトライ / キャラクターシートを生成） */
  label: string;
  /** 選択中モデルの消費ポイント（バッジ表示用） */
  selectedCredits: number | null;
  isGenerating: boolean;
  /** 生成不可（プロンプト未入力等）。生成中とは別管理 */
  disabled?: boolean;
  onGenerate: () => void;
  model: ActorModel;
  onModelChange: (model: ActorModel) => void;
  /** モデル別の消費ポイント（ドロップダウン項目に併記） */
  creditByModel: Record<ActorModel, number | null>;
  /** 同一画面に複数設置される場合に備えた一意 ID */
  dropdownId: string;
};

// AI俳優生成が対応する画像モデルのコード。表示名(ラベル)は generation_models
// マスタの display_label を参照するためここには持たない。
const ACTOR_MODEL_CODES: ActorModel[] = ['gpt-image-2.5-flare-auto', 'gemini-3-pro-image-preview'];

// model_code のプレフィックスからプロバイダ見出し (グループ名 + ロゴ) を解決する。
// 画像モデルのマスタ行には動画側のような metadata.ui_group_label が無いため、
// コード側で導出する。ロゴは mask-image 表示で塗りはテキスト色に追従する。
function resolveProviderGroup(modelCode: string): { label: string; iconMask?: string } {
  if (modelCode.startsWith('gpt')) return { label: 'OpenAI', iconMask: '/openai_icon.svg' };
  if (modelCode.startsWith('gemini')) return { label: 'Google Gemini', iconMask: '/gemini_icon_white.svg' };
  return { label: modelCode };
}

/**
 * モデル選択ドロップダウン付き生成ボタン。
 * studio シナリオ編集画面の「最初のフレームを生成」ボタンと同じスプリットボタン構造
 * （メインボタン + ▼ トリガ + ポータルメニュー）。
 */
export function ActorGenerateButton({
  label,
  selectedCredits,
  isGenerating,
  disabled,
  onGenerate,
  model,
  onModelChange,
  creditByModel,
  dropdownId,
}: Props) {
  const dropdown = usePortalDropdown({
    triggerClassName: styles.frameModelDropdownTrigger,
    groupClassName: styles.frameGenerateGroup,
    align: 'right',
  });

  // モデル表示名は generation_models マスタ (display_label) から取得。
  // 対応コードのみに絞り、マスタの sort_order 順を維持する。
  const { data: imageModels } = useGenerationModels('image');
  const modelOptions = useMemo<{ code: ActorModel; label: string }[]>(
    () =>
      (imageModels ?? [])
        .filter((m) => ACTOR_MODEL_CODES.includes(m.model_code as ActorModel))
        .map((m) => ({
          code: m.model_code as ActorModel,
          label: m.display_label,
        })),
    [imageModels],
  );

  // シナリオ編集画面の「動画を生成」ドロップダウンと同じく、プロバイダ見出し +
  // グループ間セパレータでグルーピングして表示する (フィルタ行は使わない)。
  const menuItems: React.ReactNode[] = [];
  {
    const familyOrder: string[] = [];
    const grouped = new Map<string, typeof modelOptions>();
    for (const opt of modelOptions) {
      const key = resolveProviderGroup(opt.code).label;
      if (!grouped.has(key)) {
        grouped.set(key, []);
        familyOrder.push(key);
      }
      grouped.get(key)!.push(opt);
    }
    familyOrder.forEach((family) => {
      const opts = grouped.get(family)!;
      if (menuItems.length > 0) {
        menuItems.push(<ModelDropdownSeparator key={`sep-${family}`} />);
      }
      menuItems.push(
        <ModelDropdownSectionHeader
          key={`hdr-${family}`}
          label={family}
          iconMask={resolveProviderGroup(opts[0].code).iconMask}
        />,
      );
      opts.forEach((opt) => {
        const pt = creditByModel[opt.code];
        menuItems.push(
          <ModelDropdownItem
            key={opt.code}
            label={opt.label}
            pts={pt !== null ? `${pt}pt` : undefined}
            active={model === opt.code}
            onSelect={() => {
              onModelChange(opt.code);
              dropdown.close();
            }}
          />,
        );
      });
    });
  }

  return (
    <>
      <div
        className={`${styles.frameGenerateGroup} ${isGenerating ? 'ai-loading-active' : ''}`}
      >
        <button
          type="button"
          className={styles.frameGenerateButton}
          onClick={onGenerate}
          disabled={isGenerating || disabled}
        >
          {isGenerating ? (
            <>
              生成中
              <span className="ai-loading-dots">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </>
          ) : (
            <>
              <span>{label}</span>
              <span className={styles.creditBadge}>{selectedCredits ?? '...'}</span>
            </>
          )}
        </button>
        <div className={styles.frameModelDropdown}>
          <button
            type="button"
            className={styles.frameModelDropdownTrigger}
            onClick={(e) => dropdown.open(dropdownId, e.currentTarget)}
            disabled={isGenerating}
            aria-label="生成モデルを選択"
          >
            <span className={styles.frameModelArrow}>▼</span>
          </button>
        </div>
      </div>

      {dropdown.openId === dropdownId && dropdown.pos && (
        <ModelDropdownMenuShell
          menuRef={dropdown.menuRef}
          pos={dropdown.pos}
          emptyText="選択できるモデルがありません"
        >
          {menuItems}
        </ModelDropdownMenuShell>
      )}
    </>
  );
}
